import Swal from "sweetalert2";
import React, { useEffect, useState, useCallback, useContext } from "react";
import axios from "axios";
import config from "../../config";
import { useParams } from "react-router-dom";
import Accordion from "../../components/Accordion";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import WarningIcon from '@mui/icons-material/Warning';
import SepticTable from "../../components/SepticTable";
import { UserContext } from "../../components/MyContext";
import { fiscalMonthOrder, formatChemicalFormula, getFiscalYear } from '../../js/global';
import { NumericFormat } from 'react-number-format';

function TabActivity({ refresh }) {
  const [activities, setActivities] = useState([]);
  const [quantities, setQuantities] = useState([]);
  const [sourcesFiles, setSourFiles] = useState("");
  const [exportSourcesFiles, setExportSourcesFiless] = useState([]);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isSystemOpenResolved, setIsSystemOpenResolved] = useState(false);
  const [countAvg, setCountAvg] = useState(null);
  const { id, years, fac_id, campus_id } = useParams();
  const [dataBOD, setDataBOD] = useState(Array(12).fill(0));
  const [hasPea, SetHasPea] = useState(false);
  const { userData } = useContext(UserContext);
  const [BODStudent, SetBODStudent] = useState(0);

  const yearBE = Number(years);
  const yearAD = yearBE - 543;

  const fetchSystemOpen = useCallback(async () => {
    try {
      const res = await axios.get(`${config.urlApi}/activity/showPeriod/${fac_id}`);
      if (res.status === 200 && res.data.length > 0) {
        const currentPeriod = res.data.find((item) => Number(item.years) === yearAD);
        const isOpenRaw = currentPeriod?.is_open;
        const isOpen = isOpenRaw === true || isOpenRaw === 1 || isOpenRaw === '1';
        setIsDisabled(!isOpen);
      } else {
        setIsDisabled(true);
      }
    } catch (err) {
      console.log('fetchSystemOpen error:', err.message);
    } finally {
      setIsSystemOpenResolved(true);
    }
  }, [fac_id, yearAD]);

  const fetchDataApi = useCallback(async () => {
    try {
      const usePrevious = yearAD >= 2026;

      const currentRes = await axios.get(
        `${config.urlApi}/scope/datasocpe/${id}`
      );

      const previousRes = usePrevious
        ? await axios.get(
          `${config.urlApi}/scope/previous-data/${fac_id}/${yearAD - 1}`
        )
        : { data: [] };
      
      const currentData = currentRes?.data;
      const previousData = previousRes?.data ?? [];

      const mergedData = currentData.map(scope => {
        const prevScope = previousData.find(p => p.id === scope.id);

        return {
          ...scope,
          headcategories: scope.headcategories
          .sort((a, b) => a.id - b.id)
          .map(head => {
            const prevHead = prevScope?.headcategories.find(
              h => h.id === head.id
            );

            return {
              ...head,
              data_scopes: [
                ...(prevHead?.data_scopes ?? []),
                ...head.data_scopes
              ]
              .sort((a, b) => {
                const fyA = getFiscalYear(a.years, a.month);
                const fyB = getFiscalYear(b.years, b.month);
                const nameCompare = (a.name ?? '').localeCompare(b.name ?? '');
                if (nameCompare !== 0) return nameCompare;

                if (fyA !== fyB) {
                  return fyA - fyB;
                }

                if (fyA >= 2026) {
                  return fiscalMonthOrder(a.month) - fiscalMonthOrder(b.month);
                }

                return a.month - b.month;
              })
            };
          })
        };
      });

      setActivities(mergedData);

      // Initialize quantities array with default values
      const initialQuantities = mergedData.map((activity) =>
        activity.headcategories.map((headCategory) =>
          headCategory.data_scopes.map((data_scope) => data_scope.quantity)
        )
      );
      setQuantities(initialQuantities);

      const response = await axios.get(config.urlApi + `/dividData/${id}`);
      setCountAvg(response.data);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
      });
    }
  }, [fac_id, id, yearAD]);

  const Thmonths = yearAD >= 2026
    ? [
      { id: 10, year: yearAD - 1, month: `ต.ค ${yearBE - 1}` },
      { id: 11, year: yearAD - 1, month: `พ.ย ${yearBE - 1}` },
      { id: 12, year: yearAD - 1, month: `ธ.ค ${yearBE - 1}` },
      { id: 1, year: yearAD, month: "ม.ค" },
      { id: 2, year: yearAD, month: "ก.พ" },
      { id: 3, year: yearAD, month: "มี.ค" },
      { id: 4, year: yearAD, month: "เม.ย" },
      { id: 5, year: yearAD, month: "พ.ค" },
      { id: 6, year: yearAD, month: "มิ.ย" },
      { id: 7, year: yearAD, month: "ก.ค" },
      { id: 8, year: yearAD, month: "ส.ค" },
      { id: 9, year: yearAD, month: "ก.ย" },
    ]
    : [
      { id: 1, year: yearAD, month: "ม.ค" },
      { id: 2, year: yearAD, month: "ก.พ" },
      { id: 3, year: yearAD, month: "มี.ค" },
      { id: 4, year: yearAD, month: "เม.ย" },
      { id: 5, year: yearAD, month: "พ.ค" },
      { id: 6, year: yearAD, month: "มิ.ย" },
      { id: 7, year: yearAD, month: "ก.ค" },
      { id: 8, year: yearAD, month: "ส.ค" },
      { id: 9, year: yearAD, month: "ก.ย" },
      { id: 10, year: yearAD, month: "ต.ค" },
      { id: 11, year: yearAD, month: "พ.ย" },
      { id: 12, year: yearAD, month: "ธ.ค" },
    ];

  const handleQuantityChange = (
    e,
    dataIndex,
    headIndex,
    activityIndex,
    quantityDataId
  ) => {
    let inputValue = e.target.value;
    const quantityDataQuantity = inputValue.toString().replace(/,/g, '');

    const newQuantities = [...quantities];
    newQuantities[activityIndex][headIndex][dataIndex] = {
      quantity: inputValue,
    };
    setQuantities(newQuantities);
    handlerSaveData(quantityDataId, quantityDataQuantity); // Pass quantityDataId and updated quantityDataQuantity to the handlerSaveData function
  };
  const handlerSaveData = async (quantityDataId, quantityDataQuantity) => {
    try {
      if (quantityDataQuantity === "" || quantityDataQuantity === null || isNaN(quantityDataQuantity)) return;
      const payload = {
        id: quantityDataId, // ใช้ค่า quantityDataId ใน payload
        quantity: quantityDataQuantity, // เพิ่ม quantityDataQuantity เข้าไปใน payload
      };
      await axios.put(config.urlApi + `/scope/updateQuantity/${id}`, payload);
      fetchDataApi();
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: e.message,
      });
    }
  };

  const fetchExportFile = useCallback(async () => {
    try {
      const response = await axios.get(config.urlApi + `/sourcesfile/${id}`);
      setExportSourcesFiless(response.data);
    } catch (e) {
      console.log(e.message);
    }
  }, [id]);

  const handdlerFuel = async () => {
    try {
      Swal.fire({
        title: "กำลังดึงข้อมูล...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await axios.put(config.urlApi + `/datascope/pullDataFuel/${id}`);

      if (response.status === 200) {
        Swal.fire({
          icon: "success",
          title: "ดึงข้อมูลสำเร็จ",
        });
        fetchDataApi();
      } else {
        Swal.fire({
          icon: "error",
          title: "ดึงข้อมูลไม่สำเร็จ",
        });
      }
    } catch (e) {
      console.log(e.message);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด"
      });
    }
  };

  const getDataPEA = async () => {
    try {
      Swal.fire({
        title: "กำลังดึงข้อมูล...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const convertYear = parseInt(years) - 543;
      const fac_central = ['MC1031', 'MH2000'];
      const resPea = await axios.get(`${config.urlApi}/datascope/get_pea/${fac_id}/${convertYear}`);

      if (resPea.status !== 200) return SetHasPea(false);

      const peaData = resPea?.data;

      if (!fac_central.includes(fac_id)) {
        if (yearAD >= 2026) {
          const previousRes = yearAD >= 2026
            ? await axios.get(`${config.urlApi}/datascope/get_pea/${fac_id}/${yearAD - 1}?fiscalYear=true`)
            : { data: [] };

          if (previousRes.status !== 200 || previousRes.data.length <= 0) return SetHasPea(false);

          const mergedPrev = mergeDataPea(previousRes.data);
          const successPrev = await updatePeaData(mergedPrev, id, fac_id, yearAD, true);
          SetHasPea(successPrev);

          return;
        }
        if (peaData.length > 0) {
          const merged = mergeDataPea(peaData);
          const success = await updatePeaData(merged, id);
          SetHasPea(success);
        } else {
          SetHasPea(false);
        }
        return;
      }

      if (fac_central.includes(fac_id)) {
        if (campus_id === 'MC') {
          if (yearAD >= 2026) {
            processPeaCampusData(yearAD - 1, campus_id, true);
            return;
          }
          processPeaCampusData(yearAD, campus_id);
        } else {
          if (yearAD >= 2026) {
            processPeaCampusData(yearAD - 1, campus_id, true);
            return;
          }
          processPeaCampusData(yearAD, campus_id);
        }
        return;
      }

      SetHasPea(false);
    } catch (error) {
      console.error(error.message);
      Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด" });
      SetHasPea(false);
    } finally {
      Swal.close();
    }
  };

  async function processPeaCampusData(year, campusId, isFiscalYear = false) {
    const billCmu = await axios.get(`${config.urlApi}/billing/cmu/${campus_id}/${year}/`);

    if (campusId === 'MC') {
      const [peaMC, peaSD] = await Promise.all([
        fetchCampusPeaData('MC', year),
        fetchCampusPeaData('SD', year)
      ]);

      let final = [];

      if (peaMC.length > 0) final = final.concat(mergeDataPea(peaMC));
      if (peaSD.length > 0) final = addPeaDataArrays(final, mergeDataPea(peaSD));

      if (final.length > 0) {

        const result = subtractPeaDataArrays(final, billCmu?.data, isFiscalYear);
        const success = isFiscalYear
          ? await updatePeaData(result, id, fac_id, yearAD, isFiscalYear)
          : await updatePeaData(result, id);
        SetHasPea(success);
      } else {
        SetHasPea(false);
      }
    } else {
      const allPeaData = await fetchCampusPeaData(campus_id, year);
      if (allPeaData.length > 0) {
        const data_pea = mergeDataPea(allPeaData);
        const result = subtractPeaDataArrays(data_pea, billCmu?.data, isFiscalYear);
        const success = isFiscalYear
          ? await updatePeaData(result, id, fac_id, yearAD, isFiscalYear)
          : await updatePeaData(result, id);
        SetHasPea(success);
      } else {
        SetHasPea(false);
      }
    }

  }

  async function updatePeaData(data, id, fac_id, yearAD, fiscalYear = false) {
    try {
      const res = await axios.put(`${config.urlApi}/datascope/update_pea_scope2/${id}?fac_id=${fac_id}&year=${yearAD}&fiscalYear=${fiscalYear}`, { peaData: data });
      if (res.status === 200) {
        fetchDataApi();
        return true;
      }
    } catch (e) {
      console.error('Update Error:', e.message);
    }
    return false;
  }

  function addPeaDataArrays(arr1, arr2) {
    const map = {};

    [...arr1, ...arr2].forEach(item => {
      const ym = (item.date_payment || item.month_bath).slice(0, 7);
      const kWh = item.kWh || item.iallkwh || 0;
      map[ym] = (map[ym] || 0) + kWh;
    });

    return Object.entries(map)
      .map(([ym, kWh]) => ({
        date_payment: ym,
        kWh: parseFloat(kWh.toFixed(2))
      }))
      .sort((a, b) => new Date(a.date_payment) - new Date(b.date_payment));
  }

  function subtractPeaDataArrays(arr1, arr2, isFiscalYear = false) {
    const map2 = Object.fromEntries(
      arr2.map(item => [(item.date_payment || item.month_bath).slice(0, 7), item.kWh || item.iallkwh || 0])
    );
    return arr1.filter(item => {
      if (!isFiscalYear) return true;

      const month = new Date(item.date_payment).getMonth() + 1;
      return month >= 10 && month <= 12; // เฉพาะ ต.ค. - ธ.ค.
    })
    .map(item => {
      const ym = item.date_payment.slice(0, 7);
      const value = map2[ym] ?? 0;
      
      return {
        date_payment: item.date_payment,
        kWh: parseFloat(Math.abs(item.kWh - value).toFixed(2))
      }
    }).sort((a, b) => new Date(a.date_payment) - new Date(b.date_payment));
  };

  function mergeDataPea(data) {
    const map = {};
    data.forEach(({ date_payment, kWh, pv }) => {
      const kWhNum = isNaN(Number(kWh)) ? 0 : Number(kWh);
      const pvNum = isNaN(Number(pv)) ? 0 : Number(pv);

      if (!map[date_payment]) {
        map[date_payment] = { 
          date_payment, 
          kWh: kWhNum, 
          pv: pvNum 
        };
      } else {
        map[date_payment].kWh += kWhNum;
        map[date_payment].pv += pvNum;
      }
    });
    return Object.values(map).sort((a, b) => new Date(a.date_payment) - new Date(b.date_payment));
  }

  async function fetchCampusPeaData(camp_id, year) {
    const res = await axios.get(`${config.urlApi}/datascope/pea_data_all/${camp_id}/${year}`);
    return res.data ?? [];
  }

  const showDataPdf = (exportSourcesFile) => {
    Swal.fire({
      icon: "question",
      title: "เปิดไฟล์",
      text: "ต้องการเปิดไฟล์ใช่หรือไม่",
      showCancelButton: true,
    }).then((res) => {
      if (res.isConfirmed) {
        window.open(
          `${config.urlApi}/sourcesfile/${exportSourcesFile}`,
          "_blank",
          "noreferrer"
        );
      }
    });
  };

  const getBadgeClass = (name) => {
    switch (name) {
      case 'scope1':
        return 'badge-primary';
      case 'scope2':
        return 'badge-warning';
      case 'scope3':
        return 'badge-success';
      case 'separate':
        return 'badge-info';
      case 'removal':
        return 'badge-danger';
      default:
        return 'badge-secondary'; // ค่าเริ่มต้นเมื่อไม่ตรงกับเงื่อนไขที่กำหนด
    }
  };

  const formatTitle = (name) => {
    if (name === 'scope1') return 'Scope 1';
    if (name === 'scope2') return 'Scope 2';
    if (name === 'scope3') return 'Scope 3';
    if (name === 'separate') return 'Separate';
    if (name === 'removal') return 'Removal';
    return '';
  }

  const handleDataFromChild = (data) => {
    setDataBOD(data?.BODByMonth);
    SetBODStudent(data?.BODStudent);

    if (activities.length === 0) return;

    activities.forEach((activity) => {
      activity.headcategories.forEach((headCategory) => {
        headCategory.data_scopes.forEach((data_scope) => {
          if (
            headCategory.id === 6 &&
            data_scope.name === 'BOD (Septic Tank)' &&
            years >= 2567
          ) {
            const quantityDataId = data_scope.id;
            const quantityDataQuantity = data[data_scope.month - 1]; // index = month - 1
            if (
              quantityDataQuantity !== "" &&
              quantityDataQuantity !== null &&
              !isNaN(quantityDataQuantity)
            ) {
              handlerSaveData({ preventDefault: () => { } }, quantityDataId, quantityDataQuantity);
            }
          }
        });
      });
    });
  };

  const hiddenItems = [
    'บุคลากร',
    'แม่บ้าน (ที่หน่วยงานจ้างเอง)',
    'รปภ.(ที่หน่วยงานจ้างเอง)',
    'ผู้เข้าพัก',
    'ผู้ใช้ห้องประชุม/สัมมนา',
    'SCH LEC',
    'SCH LAB',
    'SCH IS/THESIS',
    'NO, Stu THESIS',
    'SCH Project',
    'จำนวนผู้ป่วยใน',
    'จำนวนผู้ป่วยนอก',
    'จำนวน Man-hour ทั้งหมด'
  ];

  function formatTitleAccordion(index, text) {
    return (
      <>
        {index + 1}.) {formatChemicalFormula(text)}
      </>
    );
  }

  //Calc Total BOD
  const monthTotal = dataBOD.map(v => parseFloat(v) || 0).reduce((sum, v) => sum + v, 0);
  const student = parseFloat(BODStudent) || 0;
  const totalBOD = (monthTotal + student);

  useEffect(() => {
    fetchDataApi();
    fetchExportFile();
  }, [id, fetchDataApi, fetchExportFile]);

  useEffect(() => {
    getDataPEA();
    // getDataPEA uses local helpers that are recreated on render; key this effect to route params instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [years, fac_id, campus_id, id, yearAD]);

  useEffect(() => {
    fetchSystemOpen();
  }, [fetchSystemOpen]);

  return (
    <div>
      <h2 className="text-center">กิจกรรมการปล่อยก๊าซเรือนกระจก</h2>
      {isSystemOpenResolved && isDisabled && (
        <div className="alert fw-bold p-3 d-flex align-items-center" role="alert" style={{ borderColor: 'yellow', backgroundColor: '#f8f5df', borderLeft: '5px solid #ffc107' }}>
          <WarningIcon size={40} className="me-2" /> ระบบปิดการกรอกข้อมูล
        </div>
      )}
      {activities.length === 0 ? (
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        activities.map((activity, activityIndex) => {
          const allowedFacultyIDs = ['HC5000', 'MC1000', 'MH2000', 'OT6000', 'SC4000', 'SD3000'];
          const shouldHide = activity.id === 5 && !allowedFacultyIDs.includes(userData?.facultyID);

          if (shouldHide) return null;
          return (
            <div className="card" key={activityIndex}>
              <div className="card-header">
                <h4 className={`badge ${getBadgeClass(activity.name)} px-4 py-1`}>{formatTitle(activity.name)}</h4>
              </div>

              <div className="accordion" id={`accordion${activity.name}`}>
                {activity.headcategories.map((headCategory, headIndex) => (
                  <Accordion
                    key={headCategory.id}
                    id={`collapse${headCategory.id}`}
                    title={formatTitleAccordion(headIndex, headCategory.head_name)}
                    expanded={headCategory.id === 0}
                  >
                    {headCategory.id === 11 ||
                      headCategory.id === 21 ||
                      headCategory.id === 30 ||
                      headCategory.id === 31 ||
                      headCategory.id === 32 ||
                      headCategory.id === 33 ? (
                      <button
                        className="btn btn-success mb-2"
                        onClick={handdlerFuel}
                        disabled={isDisabled}
                      >
                        <CompareArrowsIcon /> ซิงค์ข้อมูล
                      </button>
                    ) : (
                      <></>
                    )}
                    {headCategory.id === 6 && years >= 2567 ? (
                      <SepticTable
                        onSendData={handleDataFromChild}
                      />
                    ) : (<></>)}
                    <div className="table-responsive">
                      <table className="table table-striped table-bordered">
                        <thead>
                          <tr className="text-center">
                            <th>รายการ</th>
                            <th>หน่วย</th>
                            {Thmonths.map((Thmonth) => (
                              <th key={Thmonth.id}>{Thmonth.month}</th>
                            ))}
                            <th>ปริมาณ <br /> {headCategory.id === 24 ? 'ตัว' : ''} /<br /> ปี</th>
                            <th>
                              (tCO<sub>2</sub>e)
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {headCategory.data_scopes.map(
                            (data_scope, dataIndex) => {
                              // ตรวจสอบว่าเป็นแถวแรกของรายการหรือไม่
                              const isFirstRow =
                                dataIndex === 0 ||
                                headCategory.data_scopes[dataIndex - 1].name !==
                                data_scope.name;
                              // Calculate total quantity per year
                              const filteredItems = headCategory.data_scopes.filter((item) => item.name === data_scope.name);
                              const totalYearQuantity = filteredItems.reduce(
                                  (total, item) =>
                                    parseFloat(total) + parseFloat(item.quantity),
                                  0
                                );
                              
                              const filteredItems2 = headCategory.data_scopes.filter(
                                (item) =>
                                  item.name === data_scope.name &&
                                  item.quantity !== null &&
                                  item.quantity !== undefined &&
                                  !isNaN(parseFloat(item.quantity)) &&
                                  parseFloat(item.quantity) !== 0
                              );
                              const averageYearQuantity = filteredItems2.length > 0
                                ? totalYearQuantity / filteredItems2.length
                                : 0;

                              const hide = isFirstRow && data_scope.name !== 'BOD โรงบำบัดน้ำเสีย';

                              return (
                                <React.Fragment key={dataIndex}>
                                  {hide && (

                                    !hiddenItems.includes(data_scope.name.trim()) && (
                                      <tr className="text-center">
                                        {
                                          !hiddenItems.includes(data_scope.name.trim()) && (
                                            <>
                                              <td>{formatChemicalFormula(data_scope.name)}</td>
                                              <td>{formatChemicalFormula(data_scope.lci)}</td>
                                            </>
                                          )
                                        }
                                        {Thmonths.map((Thmonth, index) => {
                                          const quantityData = headCategory.data_scopes.find(
                                            (item) =>
                                              item.month === Thmonth.id &&
                                              item.name === data_scope.name
                                          );
                                          return (
                                            <td key={Thmonth.id}>

                                              {data_scope.head_id === 6 && hiddenItems.includes(data_scope.name.trim()) ? ('')
                                                : data_scope.head_id === 11 ||
                                                  data_scope.head_id === 21 ||
                                                  data_scope.head_id === 30 ||
                                                  data_scope.head_id === 31 ||
                                                  data_scope.head_id === 32 ||
                                                  data_scope.head_id === 33 ? (
                                                  <form>
                                                    <NumericFormat
                                                      thousandSeparator={true}
                                                      allowNegative={false}
                                                      disabled
                                                      style={{
                                                        borderRadius: "10px",
                                                        border: "1px solid gray",
                                                        width: "90px",
                                                        padding: "5px",
                                                      }}
                                                      className="form-control"
                                                      key={data_scope.id}
                                                      value={quantityData?.quantity ?? ""}
                                                    />
                                                  </form>
                                                ) : data_scope.head_id === 6 && data_scope.name === 'BOD (Septic Tank)' && years >= 2567 ? (
                                                  <form>
                                                    <NumericFormat
                                                      thousandSeparator={true}
                                                      allowNegative={false}
                                                      disabled
                                                      style={{
                                                        borderRadius: "10px",
                                                        width: "90px",
                                                        padding: "5px",
                                                      }}
                                                      className="form-control"
                                                      key={data_scope.id}
                                                      value={years >= 2567 ? dataBOD[index] : quantityData ? quantityData?.quantity : ""}
                                                    />
                                                    </form>
                                                  ) : data_scope.head_id === 8 && (
                                                    (data_scope.name === 'Electricity from PEA' && hasPea) ||
                                                    data_scope.name === 'Solar Cell บางจาก'
                                                  ) ? (
                                                    <form>
                                                      <NumericFormat
                                                        thousandSeparator={true}
                                                        allowNegative={false}
                                                        disabled
                                                        style={{
                                                          borderRadius: "10px",
                                                          width: "90px",
                                                          padding: "5px",
                                                        }}
                                                        className="form-control"
                                                        key={data_scope.id}
                                                        value={quantityData ? quantityData?.quantity : ""}
                                                      />
                                                    </form>
                                                ) : (
                                                  <form>
                                                    <NumericFormat
                                                      disabled={isDisabled}
                                                      style={{
                                                        borderRadius: "10px",
                                                        border: "1px solid gray",
                                                        width: "90px",
                                                        padding: "5px",
                                                      }}
                                                      className="form-control"
                                                      key={data_scope.id}
                                                      value={quantityData ? quantityData?.quantity : ""}
                                                      onFocus={(e) => {
                                                        e.target.style.backgroundColor =
                                                          "#DBF1C0";
                                                      }}
                                                      thousandSeparator={true}
                                                      allowNegative={false}
                                                      onValueChange={(values) => handleQuantityChange({ target: { value: values.value } }, dataIndex, headIndex, activityIndex, quantityData.id)}
                                                    />
                                                  </form>
                                                )}
                                            </td>
                                          );
                                        })}
                                        {
                                          !hiddenItems.includes(data_scope.name.trim()) && (
                                            <>
                                              <td>
                                                {
                                                  // console.log(data_scope)
                                                  data_scope.name === "CH4 จากน้ำขังในพื้นที่นา" && countAvg !== null && countAvg !== 0
                                                    ? Number((parseFloat(totalYearQuantity) / countAvg).toFixed(2)).toLocaleString()
                                                    : data_scope.name === 'BOD (Septic Tank)' && years >= 2567
                                                    ? totalBOD.toFixed(2).toLocaleString()
                                                    : headCategory.id === 24 || headCategory.id === 25
                                                    ? Number(parseFloat(averageYearQuantity).toFixed(2)).toLocaleString()
                                                    : Number(parseFloat(totalYearQuantity).toFixed(2)).toLocaleString()
                                                }
                                              </td>
                                              {/* (tCO2e) */}
                                              {activity.name === "scope1" ? (
                                                <td>
                                                  {/* เงื่อนไขค้น จากชื่อ  เช็คค่า countAvg ไม่เท่ากับ  null และ 0 */}
                                                  {data_scope.name ===
                                                    "CH4 จากน้ำขังในพื้นที่นา" &&
                                                    countAvg !== null &&
                                                    countAvg !== 0
                                                    ? (Number(parseFloat((totalYearQuantity / countAvg) * data_scope.EF) / 1000).toFixed(2)).toLocaleString()
                                                    : data_scope.name === 'BOD (Septic Tank)' && years >= 2567
                                                    ? (parseFloat(totalBOD * data_scope.EF) / 1000).toFixed(2).toLocaleString()
                                                    : data_scope.name === 'BOD โรงบำบัดน้ำเสีย'
                                                    ? (Number(parseFloat(totalYearQuantity * data_scope.kgCO2e) / 1000).toFixed(2)).toLocaleString()
                                                    : (Number(parseFloat(totalYearQuantity * data_scope.EF) / 1000).toFixed(2)).toLocaleString()
                                                  }
                                                </td>
                                              ) : (
                                                <td>
                                                  {
                                                    data_scope.head_id === 34
                                                    ? Number(parseFloat(totalYearQuantity).toFixed(2)).toLocaleString()
                                                    : (Number(parseFloat(totalYearQuantity * data_scope.kgCO2e) / 1000).toFixed(2)).toLocaleString()
                                                  }
                                                </td>
                                              )}
                                            </>
                                          )
                                        }
                                      </tr>
                                    )
                                  )}
                                </React.Fragment>
                              );
                            }
                          )}

                          {headCategory.id === 8 && (
                            <tr
                              className="text-center fw-bold"
                              style={{ backgroundColor: '#f9f9f9' }}
                            >
                              <td>สรุปการใช้ไฟฟ้า Scope2</td>
                              <td>kWh</td>

                              {Thmonths.map((Thmonth) => {
                                const pea1 =
                                  headCategory.data_scopes.find(
                                    (item) =>
                                      item.name === 'Electricity from PEA' &&
                                      item.month === Thmonth.id
                                  )?.quantity || 0;

                                const pea2 =
                                  headCategory.data_scopes.find(
                                    (item) =>
                                      item.name ===
                                      'ไฟฟ้าจาก PEA นอกหนือจากกองอาคารเรียกเก็บ' &&
                                      item.month === Thmonth.id
                                  )?.quantity || 0;

                                const tenant =
                                  headCategory.data_scopes.find(
                                    (item) =>
                                      item.name ===
                                      'ไฟฟ้าที่เรียกเก็บเงินจากผู้เช่าพื้นที่/อาคาร' &&
                                      item.month === Thmonth.id
                                  )?.quantity || 0;

                                const summary = (parseFloat(pea1) + parseFloat(pea2)) - parseFloat(tenant);
                                const safeSummary = Math.max(0, summary);

                                return (
                                  <td key={Thmonth.id}>
                                    <NumericFormat
                                      disabled
                                      value={safeSummary !== 0 ? safeSummary : 0.00}
                                      style={{
                                        borderRadius: "10px",
                                        width: "90px",
                                        padding: "5px",
                                      }}
                                      className="form-control"
                                      thousandSeparator={true}
                                      allowNegative={false}
                                    />
                                  </td>
                                );
                              })}

                              {/* total per year */}
                              <td>
                                {(() => {
                                  const totalYear = Thmonths.reduce((sum, Thmonth) => {
                                    const pea1 =
                                      headCategory.data_scopes.find(
                                        (item) =>
                                          item.name === 'Electricity from PEA' &&
                                          item.month === Thmonth.id
                                      )?.quantity || 0;

                                    const pea2 =
                                      headCategory.data_scopes.find(
                                        (item) =>
                                          item.name ===
                                          'ไฟฟ้าจาก PEA นอกหนือจากกองอาคารเรียกเก็บ' &&
                                          item.month === Thmonth.id
                                      )?.quantity || 0;

                                    const tenant =
                                      headCategory.data_scopes.find(
                                        (item) =>
                                          item.name ===
                                          'ไฟฟ้าที่เรียกเก็บเงินจากผู้เช่าพื้นที่/อาคาร' &&
                                          item.month === Thmonth.id
                                      )?.quantity || 0;

                                    return (
                                      sum +
                                      (parseFloat(pea1) + parseFloat(pea2)) -
                                      parseFloat(tenant)
                                    );
                                  }, 0);

                                  return Number(totalYear.toFixed(2)).toLocaleString();
                                })()}
                              </td>

                              {/* tCO2e */}
                              <td>
                                {(() => {
                                  const totalYear = Thmonths.reduce((sum, Thmonth) => {
                                    const pea1 =
                                      headCategory.data_scopes.find(
                                        (item) =>
                                          item.name === 'Electricity from PEA' &&
                                          item.month === Thmonth.id
                                      )?.quantity || 0;

                                    const pea2 =
                                      headCategory.data_scopes.find(
                                        (item) =>
                                          item.name ===
                                          'ไฟฟ้าจาก PEA นอกหนือจากกองอาคารเรียกเก็บ' &&
                                          item.month === Thmonth.id
                                      )?.quantity || 0;

                                    const tenant =
                                      headCategory.data_scopes.find(
                                        (item) =>
                                          item.name ===
                                          'ไฟฟ้าที่เรียกเก็บเงินจากผู้เช่าพื้นที่/อาคาร' &&
                                          item.month === Thmonth.id
                                      )?.quantity || 0;

                                    return (
                                      sum +
                                      (parseFloat(pea1) + parseFloat(pea2)) -
                                      parseFloat(tenant)
                                    );
                                  }, 0);

                                  const EF = 0.4999;

                                  return (
                                    Number((totalYear * EF) / 1000).toFixed(2)
                                  ).toLocaleString();
                                })()}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </Accordion>
                ))}
              </div>
            </div>
          )
        })
      )}
      <div className="card shadow text-center">
        <div
          className="card-header text-white"
          style={{
            background:
              " linear-gradient(346deg, rgba(127,113,168,1) 0%, rgba(71,60,152,1) 100%)",
            marginTop: "auto",
          }}
        >
          Uploads ไฟล์เอกสารหลักฐาน ( maximum size: 2 MB )
        </div>
        <div
          className="card-body"
          style={{
            background:
              " linear-gradient(346deg, rgba(127,113,168,1) 0%, rgba(71,60,152,1) 100%)",
            marginTop: "auto",
          }}
        >
          <div className="mb-3">
            {exportSourcesFiles.length === 0 ? (
              <p className="text-center text-white">ไม่มีหลักฐานข้อมูล</p>
            ) : (
              <>
                {exportSourcesFiles.map((exportSourcesFile, index) => (
                  <div key={index}>
                    <button
                      className="btn btn-primary m-3"
                      onClick={() => showDataPdf(exportSourcesFile.file_name)}
                    >
                      <DownloadForOfflineIcon /> หลักฐาน
                    </button>
                    <span className="text-white">
                      {exportSourcesFile.file_name}
                    </span>
                    <hr className="text-white" />
                  </div>
                ))}
              </>
            )}
            <br />

            <div className="input-group">
              <input
                type="file"
                className="form-control"
                onChange={(e) => setSourFiles(e.target.files[0])}
                id="inputGroupFile04"
                aria-describedby="inputGroupFileAddon04"
                accept="application/pdf"
                aria-label="Upload"
                disabled={isDisabled}
              />
              <button
                className={sourcesFiles === "" ? "btn btn-secondary" : "btn btn-primary"}
                type="button"
                id="inputGroupFileAddon04"
                disabled={sourcesFiles === "" || isDisabled}
              >
                เพิ่มหลักฐาน
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TabActivity;
